import { z } from 'zod';
import { CURRENCIES } from '../money/money';
import { defineListQuery, inForceFilter, listFilter } from './list-query.schemas';

/**
 * Fixed monthly charges (gitaumoses4/chukua#183): what a charge type costs a truck each month, under a management
 * agreement, posted to the ledger on the 1st. The kinds are rows (`fixed_charge_types`), not an enum here.
 */

const amountMinorSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);

/** `POST /agreements/:agreementId/fixed-charge-rates` — `truckId` narrows it to one truck, left out it is the agreement's default for every truck. A currency left out is the company's. */
export const createFixedChargeRateInputSchema = z.object({
  chargeTypeId: z.uuid(),
  truckId: z.uuid().optional(),
  amountMinor: amountMinorSchema,
  currency: z.enum(CURRENCIES).optional(),
  validFrom: z.iso.date(),
});
export type CreateFixedChargeRateInput = z.infer<typeof createFixedChargeRateInputSchema>;

/** `POST /fixed-charge-rates/:id/supersede` — a new amount from `effectiveDate`; the type and truck carry over. */
export const supersedeFixedChargeRateInputSchema = z.object({
  effectiveDate: z.iso.date(),
  amountMinor: amountMinorSchema,
  currency: z.enum(CURRENCIES).optional(),
});
export type SupersedeFixedChargeRateInput = z.infer<typeof supersedeFixedChargeRateInputSchema>;

/** `GET /agreements/:agreementId/fixed-charge-rates` — search matches the charge type's name. */
export const fixedChargeRateListQuerySchema = defineListQuery({
  sortFields: ['validFrom', 'amountMinor', 'chargeType'],
  defaultSort: { sort: 'validFrom', order: 'desc' },
  filters: { chargeTypeId: listFilter(z.uuid()), truckId: listFilter(z.uuid()), current: inForceFilter },
});
export type FixedChargeRateListQuery = z.infer<typeof fixedChargeRateListQuerySchema>;

/** `GET /ledger-entries` — search matches the description. */
export const ledgerEntryListQuerySchema = defineListQuery({
  sortFields: ['postedAt', 'period', 'amountMinor', 'kind'],
  defaultSort: { sort: 'postedAt', order: 'desc' },
  filters: { truckId: listFilter(z.uuid()), kind: listFilter(z.string().min(1)), accountingPeriodId: listFilter(z.uuid()) },
});
export type LedgerEntryListQuery = z.infer<typeof ledgerEntryListQuerySchema>;

/** `POST /ledger-entries/:id/adjustments` — signed: the opposite sign of the entry reverses it, the same sign adds to it. A reason is always required. */
export const adjustLedgerEntryInputSchema = z.object({
  amountMinor: z
    .number()
    .int()
    .refine((value) => value !== 0, 'An adjustment must change the amount'),
  reason: z.string().trim().min(1).max(500),
});
export type AdjustLedgerEntryInput = z.infer<typeof adjustLedgerEntryInputSchema>;
